import {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { useCallback, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getCustomSchemas,
  getAllGPanelRoles,
  UserKeys,
} from '../../../api/users-api';
import {
  employeeInfoFields,
  profileFieldOptions,
  ProfileFieldOption,
} from '../../../shared/shared-profile-field-filter';
import { calendarSharingFields } from '../../../shared/shared-profile-field-filter';
import type { RulesEngineFormValues } from './rules-engine-form-page';
import SharedButton from '../../../shared/buttons/shared-button';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import PersonalCalendarConditionBlock from './personal-calendar-condition-block';
import DriveConditionBlock from './drive-condition-block';
import GroupConditionBlock from './group-condition-block';
import ConditionBlockRow from './condition-block-row';
import {
  CALENDAR_FIELD_INFO_TYPES,
  DRIVE_FIELD_INFO_TYPES,
  GROUP_FIELD_INFO_TYPES,
  USER_FIELD_INFO_TYPES,
} from './condition-block-constants';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import FilterAlt from '@mui/icons-material/FilterAlt';

// Utility to generate unique IDs
function generateId(): string {
  return Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
}

export interface ConditionBlocksProps {
  control: Control<RulesEngineFormValues>;
  watch: UseFormWatch<RulesEngineFormValues>;
  setValue: UseFormSetValue<RulesEngineFormValues>;
  errors?: FieldErrors<RulesEngineFormValues>;
  domains: string[];
  isPersonalCalendar: boolean;
}

export default function ConditionBlocks({
  control,
  watch,
  setValue,
  errors,
  domains,
  isPersonalCalendar,
}: ConditionBlocksProps) {
  const theme = useTheme();
  const conditionBlocks = watch('conditionBlocks') || [];
  const triggerGroup = watch('triggerInfo.triggerGroup');
  const triggerEvent = watch('triggerInfo.triggerEvent');

  const isGroupAdminEvent = useMemo(() => {
    return (
      triggerGroup === 'admin' &&
      [
        'ADD_GROUP_MEMBER',
        'REMOVE_GROUP_MEMBER',
        'UPDATE_GROUP_MEMBER',
      ].includes(triggerEvent || '')
    );
  }, [triggerGroup, triggerEvent]);

  const fieldInfoTypes = useMemo(() => {
    if (triggerGroup === 'groupEvents' || isGroupAdminEvent) {
      return GROUP_FIELD_INFO_TYPES;
    }
    switch (triggerGroup) {
      case 'personalCalendar':
        return CALENDAR_FIELD_INFO_TYPES;
      case 'drive':
        return DRIVE_FIELD_INFO_TYPES;
      case 'admin':
        return [...USER_FIELD_INFO_TYPES, { label: 'Groups', value: 'groups' }];
      default:
        return USER_FIELD_INFO_TYPES;
    }
  }, [triggerGroup, isGroupAdminEvent]);

  const filterText = useMemo(() => {
    if (triggerGroup === 'groupEvents' || isGroupAdminEvent) {
      return 'Group';
    }
    if (triggerGroup === 'admin') {
      return 'Admin';
    }
    if (triggerGroup === 'drive') {
      return 'Drive';
    }
    if (triggerGroup === 'calendar') {
      return 'Calendar';
    }
    return 'Filter';
  }, [triggerGroup, isGroupAdminEvent]);

  const chipStyles = useMemo(() => {
    if (!triggerGroup) return {};
    const group = triggerGroup.toLowerCase();

    switch (group) {
      case 'admin':
        return {
          color: 'primary.main',
          borderColor: 'primary.main',
        };
      case 'drive':
        return {
          color: 'success.main',
          borderColor: 'success.main',
        };
      case 'groupevents':
      case 'group':
        return {
          color: '#9c27b0',
          borderColor: '#9c27b0',
        };
      case 'calendar':
        return {
          color: theme.palette.colors.violet[500],
          borderColor: theme.palette.colors.violet[500],
        };
      default:
        return {};
    }
  }, [triggerGroup]);

  // Query custom attribute schemas dynamically
  const { data: customSchemas } = useQuery({
    queryKey: ['customSchemas'],
    queryFn: getCustomSchemas,
  });

  const { data: gPanelRoles } = useQuery({
    queryKey: [UserKeys.gPanelRoles],
    queryFn: getAllGPanelRoles,
  });

  // Map option values dynamically to match RulesEngineAdminFiltersStep
  const customOptions = useMemo(() => {
    return (customSchemas ?? []).flatMap((schema) =>
      (schema.fields ?? []).map((field) => ({
        label: `${schema.displayName} - ${field.displayName}`,
        value: `customAttribute.${schema.schemaName}.${field.fieldName}`,
      }))
    );
  }, [customSchemas]);

  // ProfileCustomOptions for type resolution in FieldValueInput
  const profileCustomOptions = useMemo<ProfileFieldOption[]>(() => {
    return (customSchemas ?? []).flatMap((schema) =>
      (schema.fields ?? []).map((field) => ({
        display: field.displayName,
        value: `customAttribute.${schema.schemaName}.${field.fieldName}`,
        fieldType: field.fieldType,
        multiValued: field.multiValued,
        schemaName: schema.displayName,
      }))
    );
  }, [customSchemas]);

  const employeeInfoOptions = useMemo(() => {
    const isUpdateAdminEvent =
      triggerGroup === 'admin' && triggerEvent === 'UPDATE';

    return employeeInfoFields
      .filter((f) => {
        if (f.value === 'recoveryPhone' || f.value === 'recoveryEmail') {
          return isUpdateAdminEvent;
        }
        return true;
      })
      .map((f) => ({
        label: f.display,
        value: f.value,
      }));
  }, [triggerGroup, triggerEvent]);

  const primaryOrgOptions = useMemo(() => {
    return profileFieldOptions.map((f) => ({
      label: f.display,
      value: f.value,
    }));
  }, []);

  const calendarSharingOptions = useMemo(() => {
    return calendarSharingFields.map((f) => ({
      label: f.display,
      value: f.value,
    }));
  }, []);

  const getFieldOptions = useCallback(
    (infoType: string) => {
      switch (infoType) {
        case 'employeeInformation':
        case 'EMPLOYEE_INFORMATION':
          return employeeInfoOptions;
        case 'primaryOrganization':
        case 'PRIMARY_ORGANIZATION':
          return primaryOrgOptions;
        case 'customAttributes':
        case 'CUSTOM_ATTRIBUTES':
          return customOptions;
        case 'calendarSharing':
          return calendarSharingOptions;
        case 'fileType':
          return [{ label: 'File Type', value: 'fileType' }];
        case 'fileSize':
          return [{ label: 'File Size', value: 'fileSize' }];
        case 'fileSharing':
          return [
            {
              label: 'user external to the domain',
              value: 'fileSharedToUserExternalToDomain',
            },
            {
              label: 'user internal to the domain',
              value: 'fileSharedToUserInternalToDomain',
            },
            {
              label: 'anyone with the link (public)',
              value: 'fileIsSharedPublic',
            },
            {
              label: 'specific user',
              value: 'fileSharedToSpecificUser',
            },
            {
              label: 'domain',
              value: 'fileIsSharedToDomain',
            },
          ];
        case 'groups':
          return [
            { label: 'Group Email', value: 'groupEmail' },
            { label: 'Group Name', value: 'groupName' },
          ];
        case 'groupName':
          return [{ label: 'Group Name', value: 'groupName' }];
        case 'memberCount':
          return [{ label: 'Member Count', value: 'memberCount' }];
        default:
          return [];
      }
    },
    [
      employeeInfoOptions,
      primaryOrgOptions,
      customOptions,
      calendarSharingOptions,
    ]
  );

  const [collapsedBlocks, setCollapsedBlocks] = useState<
    Record<string, boolean>
  >({});

  const toggleCollapse = useCallback((blockId: string) => {
    setCollapsedBlocks((prev) => ({
      ...prev,
      [blockId]: !prev[blockId],
    }));
  }, []);

  const handleAddBlock = useCallback(() => {
    const isGroupAdminEvent =
      triggerGroup === 'admin' &&
      [
        'ADD_GROUP_MEMBER',
        'REMOVE_GROUP_MEMBER',
        'UPDATE_GROUP_MEMBER',
      ].includes(triggerEvent || '');

    const defaultFieldInfoType = isPersonalCalendar
      ? 'calendarSharing'
      : triggerGroup === 'drive'
      ? 'fileType'
      : triggerGroup === 'groupEvents' || isGroupAdminEvent
      ? 'groupName'
      : 'employeeInformation';

    const defaultFieldName =
      triggerGroup === 'drive'
        ? 'fileType'
        : triggerGroup === 'groupEvents' || isGroupAdminEvent
        ? 'groupName'
        : isPersonalCalendar
        ? 'sharedToUserInternalToDomain'
        : '';

    const defaultOperator =
      defaultFieldInfoType === 'fileType'
        ? ('equals' as const)
        : ('contains' as const);

    const isBooleanShareType = [
      'sharedToUserInternalToDomain',
      'sharedToUserExternalToDomain',
      'fileSharedToUserExternalToDomain',
      'fileSharedToUserInternalToDomain',
      'fileIsSharedPublic',
    ].includes(defaultFieldName);

    const newBlock = {
      id: generateId(),
      conditions: [
        {
          id: generateId(),
          fieldInfoType: defaultFieldInfoType,
          fieldName: defaultFieldName,
          operator: defaultOperator,
          value: isBooleanShareType ? 'true' : '',
        },
      ],
    };
    setValue('conditionBlocks', [...conditionBlocks, newBlock]);
  }, [
    conditionBlocks,
    setValue,
    isPersonalCalendar,
    triggerGroup,
    triggerEvent,
  ]);

  const handleRemoveBlock = useCallback(
    (blockIndex: number) => {
      const updatedBlocks = conditionBlocks.filter(
        (_, index) => index !== blockIndex
      );
      setValue('conditionBlocks', updatedBlocks);
    },
    [conditionBlocks, setValue]
  );

  const handleAddCondition = useCallback(
    (blockIndex: number) => {
      const conditions = conditionBlocks[blockIndex]?.conditions || [];
      const isGroupAdminEvent =
        triggerGroup === 'admin' &&
        [
          'ADD_GROUP_MEMBER',
          'REMOVE_GROUP_MEMBER',
          'UPDATE_GROUP_MEMBER',
        ].includes(triggerEvent || '');

      const defaultFieldInfoType = isPersonalCalendar
        ? 'calendarSharing'
        : triggerGroup === 'drive'
        ? 'fileType'
        : triggerGroup === 'groupEvents' || isGroupAdminEvent
        ? 'groupName'
        : 'employeeInformation';
      const defaultFieldName =
        triggerGroup === 'drive'
          ? 'fileType'
          : triggerGroup === 'groupEvents' || isGroupAdminEvent
          ? 'groupName'
          : isPersonalCalendar
          ? 'sharedToUserInternalToDomain'
          : '';

      const defaultOperator =
        defaultFieldInfoType === 'fileType'
          ? ('equals' as const)
          : ('contains' as const);

      const isBooleanShareType = [
        'sharedToUserInternalToDomain',
        'sharedToUserExternalToDomain',
        'fileSharedToUserExternalToDomain',
        'fileSharedToUserInternalToDomain',
        'fileIsSharedPublic',
      ].includes(defaultFieldName);

      const newCondition = {
        id: generateId(),
        fieldInfoType: defaultFieldInfoType,
        fieldName: defaultFieldName,
        operator: defaultOperator,
        value: isBooleanShareType ? 'true' : '',
      };
      // Explicitly set the preceding condition's logicalOperator to 'AND' if not already set
      const updatedConditions = conditions.map((cond, idx) => {
        if (idx === conditions.length - 1 && !cond.logicalOperator) {
          return { ...cond, logicalOperator: 'AND' as const };
        }
        return cond;
      });
      const updatedBlock = {
        ...conditionBlocks[blockIndex],
        conditions: [...updatedConditions, newCondition],
      };
      const updatedBlocks = [...conditionBlocks];
      updatedBlocks[blockIndex] = updatedBlock as any;
      setValue('conditionBlocks', updatedBlocks);
    },
    [conditionBlocks, setValue, isPersonalCalendar, triggerGroup, triggerEvent]
  );

  return (
    <Stack gap={2} width="100%">
      {conditionBlocks.length === 0 && (
        <Card
          variant="outlined"
          sx={{
            border: `1px dashed ${theme.palette.semantic.outline.light}`,
            padding: '24px 8px',
            color: theme.palette.semantic.text.title2,
            backgroundColor: theme.palette.semantic.surface.bg2,
          }}
        >
          <CardContent>
            <Stack
              gap={2}
              alignItems="center"
              justifyContent="center"
              sx={{ textAlign: 'center' }}
            >
              <FilterAlt aria-hidden fontSize="small" />
              <Typography variant="body2">
                No conditions added. Without filters, this rule will fire for
                every matching event across your organization.
              </Typography>
              <SharedButton
                startIcon={<AddIcon />}
                variant="secondary"
                onClick={handleAddBlock}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                {filterText === 'Filter'
                  ? 'Add Filter'
                  : `${filterText} Filter`}
              </SharedButton>
            </Stack>
          </CardContent>
        </Card>
      )}

      {conditionBlocks.map((block, blockIndex) => (
        <Box key={block.id}>
          {/* Divider with "Or" label between blocks, except for the first block */}
          {blockIndex > 0 && (
            <Divider sx={{ my: 3 }}>
              <Typography
                variant="button"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Or
              </Typography>
            </Divider>
          )}

          <Card
            sx={{
              mb: 2,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'background.default'
                  : 'grey.50',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              {/* Block Header with Conditions Match Label, Collapse, and Delete */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`${filterText}`}
                    size="small"
                    variant="outlined"
                    sx={chipStyles}
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Conditions Match
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Tooltip
                    title={
                      collapsedBlocks[block.id]
                        ? 'Expand block'
                        : 'Collapse block'
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={() => toggleCollapse(block.id)}
                    >
                      {collapsedBlocks[block.id] ? (
                        <ExpandMore />
                      ) : (
                        <ExpandLess />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete block">
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveBlock(blockIndex)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>

            {/* Collapsible Content */}
            <Collapse in={!collapsedBlocks[block.id]}>
              <Divider />
              <Box
                sx={{
                  backgroundColor:
                    theme.palette.colors?.info?.[100] ||
                    theme.palette.grey[200],
                  width: '100%',
                  mb: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    p: 1,
                    display: 'block',
                    fontStyle: 'italic',
                    color:
                      theme.palette.colors?.neutral?.[700] ||
                      theme.palette.text.secondary,
                  }}
                >
                  Add a condition to see your filter logic here.
                </Typography>
              </Box>
              <CardContent sx={{ p: 2, pt: 2 }}>
                <Stack gap={2}>
                  {/* Conditions within this block */}
                  {isPersonalCalendar ? (
                    <PersonalCalendarConditionBlock
                      blockIndex={blockIndex}
                      control={control}
                      setValue={setValue}
                      conditions={conditionBlocks[blockIndex]?.conditions || []}
                      conditionBlocks={conditionBlocks}
                      domains={domains}
                    />
                  ) : triggerGroup === 'drive' ? (
                    <DriveConditionBlock
                      blockIndex={blockIndex}
                      control={control}
                      setValue={setValue}
                      conditions={conditionBlocks[blockIndex]?.conditions || []}
                      conditionBlocks={conditionBlocks}
                      domains={domains}
                      getFieldOptions={getFieldOptions}
                      customOptions={profileCustomOptions}
                      fieldInfoTypes={fieldInfoTypes}
                      triggerGroup={triggerGroup}
                      gPanelRoles={gPanelRoles || []}
                    />
                  ) : triggerGroup === 'groupEvents' || isGroupAdminEvent ? (
                    <GroupConditionBlock
                      blockIndex={blockIndex}
                      control={control}
                      setValue={setValue}
                      conditions={conditionBlocks[blockIndex]?.conditions || []}
                      conditionBlocks={conditionBlocks}
                      triggerGroup={triggerGroup || ''}
                    />
                  ) : (
                    <ConditionBlockRow
                      blockIndex={blockIndex}
                      blockConditions={
                        conditionBlocks[blockIndex]?.conditions || []
                      }
                      conditionBlocks={conditionBlocks}
                      control={control}
                      setValue={setValue}
                      getFieldOptions={getFieldOptions}
                      customOptions={profileCustomOptions}
                      fieldInfoTypes={fieldInfoTypes}
                      domains={domains}
                      triggerGroup={triggerGroup}
                      isGroupAdminEvent={isGroupAdminEvent}
                      gPanelRoles={gPanelRoles || []}
                    />
                  )}

                  {(
                    (errors?.conditionBlocks as any)?.[blockIndex]
                      ?.conditions as any
                  )?.message && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      {
                        (
                          (errors?.conditionBlocks as any)?.[blockIndex]
                            ?.conditions as any
                        )?.message
                      }
                    </Typography>
                  )}

                  {/* Add Condition Button */}
                  <Box>
                    <SharedButton
                      startIcon={<AddIcon />}
                      size="small"
                      variant="secondary"
                      onClick={() => handleAddCondition(blockIndex)}
                      sx={{ mt: 1, textTransform: 'none' }}
                    >
                      Add condition
                    </SharedButton>
                  </Box>
                </Stack>
              </CardContent>
            </Collapse>
          </Card>
        </Box>
      ))}

      {/* Add Condition Block Button */}
      {conditionBlocks.length > 0 && (
        <SharedButton
          startIcon={<AddIcon />}
          variant="secondary"
          onClick={handleAddBlock}
          sx={{ alignSelf: 'flex-start', mt: 1, textTransform: 'none' }}
        >
          Add Condition Block
        </SharedButton>
      )}
    </Stack>
  );
}
